import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { ARTICLES } from "@/data/articles";

const formatDate = (iso, lang) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(lang === "it" ? "it-IT" : "en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch (_e) {
        return iso;
    }
};

const InsightCard = ({ article, index, lang, t }) => (
    <Link
        to={`/insights/${article.slug}`}
        data-testid={`insight-card-${article.slug}`}
        className="group relative flex flex-col bg-black border border-white/10 rounded-sm overflow-hidden hover:border-fw/60 transition-all duration-500"
    >
        <div className="relative aspect-[16/10] overflow-hidden bg-fw/[0.06]">
            <picture>
                {article.imageWebp && (
                    <source srcSet={article.imageWebp} type="image/webp" />
                )}
                <img
                    src={article.image}
                    alt={article.imageAlt[lang]}
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="500"
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.03] transition-all duration-700"
                />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10 pointer-events-none" />
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/70 backdrop-blur-sm border border-fw/40 rounded-sm font-mono text-[10px] uppercase tracking-[0.24em] text-fw">
                <Sparkles className="w-3 h-3" strokeWidth={2} />
                {article.category[lang]}
            </span>
            <span className="absolute bottom-4 left-4 font-heavy text-5xl md:text-6xl text-white/95 tabular-nums leading-none">
                {String(index + 1).padStart(2, "0")}
            </span>
        </div>

        <div className="flex-1 flex flex-col p-6 md:p-7">
            <div className="flex items-center gap-3 mb-3 font-mono text-[10px] uppercase tracking-[0.24em] text-white/45">
                <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-3 h-3" strokeWidth={2} />
                    {article.readTime} {t.insights.readTimeLabel}
                </span>
                <span className="w-1 h-1 rounded-full bg-fw" aria-hidden="true" />
                <span>{formatDate(article.publishedAt, lang)}</span>
            </div>

            <h3 className="font-display text-xl md:text-2xl font-semibold leading-[1.15] text-white text-balance mb-3 group-hover:text-fw transition-colors duration-300">
                {article.title[lang]}
            </h3>
            <p className="text-sm md:text-[15px] leading-relaxed text-white/65 flex-1 mb-5">
                {article.excerpt[lang]}
            </p>

            <span className="inline-flex items-center justify-between gap-2 mt-auto pt-4 border-t border-white/10 font-mono text-[11px] uppercase tracking-[0.22em] text-fw group-hover:text-white transition-colors">
                <span className="flex items-center gap-2">
                    {t.insights.readArticle}
                </span>
                <ArrowUpRight
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={2}
                />
            </span>
        </div>
    </Link>
);

export const Insights = () => {
    const { t, lang } = useLanguage();
    const [showAll, setShowAll] = useState(false);
    const INITIAL = 2;
    const articlesToShow = showAll ? ARTICLES : ARTICLES.slice(0, INITIAL);
    const remaining = ARTICLES.length - INITIAL;

    return (
        <section
            id="insights"
            data-testid="insights-section"
            className="relative bg-black text-white py-12 md:py-16 lg:py-20 overflow-hidden"
        >
            <div className="absolute inset-0 grid-lines opacity-25 pointer-events-none" />
            <div className="absolute -top-32 left-1/3 w-[520px] h-[520px] rounded-full bg-fw/10 blur-[180px] pointer-events-none" />

            <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-6 md:mb-8">
                    <div className="col-span-12 lg:col-span-8">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="w-10 h-px bg-fw" />
                            <span
                                data-testid="insights-overline"
                                className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw"
                            >
                                {t.insights.overline}
                            </span>
                        </div>
                        <h2
                            data-testid="insights-headline"
                            className="font-heavy text-3xl md:text-4xl lg:text-5xl leading-[0.98] uppercase text-balance"
                        >
                            {t.insights.headline}
                        </h2>
                        <p
                            data-testid="insights-description"
                            className="mt-3 text-sm md:text-base text-white/70 leading-relaxed max-w-2xl"
                        >
                            {t.insights.description}
                        </p>
                    </div>
                    <div className="hidden lg:flex col-span-4 items-end justify-end">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 text-right">
                            {t.insights.signature}
                        </span>
                    </div>
                </div>

                <div
                    data-testid="insights-grid"
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
                >
                    {articlesToShow.map((a, i) => (
                        <InsightCard key={a.slug} article={a} index={i} lang={lang} t={t} />
                    ))}
                </div>

                {remaining > 0 && (
                    <div className="mt-6 md:mt-8 flex justify-center">
                        <button
                            type="button"
                            onClick={() => setShowAll((v) => !v)}
                            data-testid="insights-show-all"
                            className="group inline-flex items-center gap-3 px-6 py-3.5 border border-white/20 bg-transparent text-white font-display font-semibold text-[11px] uppercase tracking-[0.22em] rounded-sm hover:border-fw hover:text-fw transition-all duration-300"
                        >
                            {showAll ? (
                                <>
                                    <ChevronUp className="w-4 h-4" strokeWidth={2} />
                                    {t.common.showLess}
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" strokeWidth={2} />
                                    {t.common.showAllArticles}
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};
